import { Fragment, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Copy, Check, UserPlus } from 'lucide-react';
import { DocTop } from '@/components/shell/DocTop';
import { Section } from '@/components/ui/Section';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatDateRo, formatNumberRo } from '@/lib/format';
import { useMe } from '@/lib/useMe';
import { ApiClientError } from '@/lib/apiClient';
import { fieldAria } from '@/lib/formA11y';
import {
  useMembers,
  useRemoveMember,
  useSetMemberPermission,
  useInvitations,
  useInvite,
  useCancelInvitation,
  useResendInvitation,
  type TeamMember,
  type TeamInvitation,
} from './api/teamHooks';

const NOMENCLATOR_EDIT = 'nomenclator.edit';

const ROLE_LABEL: Record<TeamMember['role'], string> = {
  owner: 'Proprietar',
  admin: 'Administrator',
  member: 'Utilizator',
};

/**
 * 2026-08-19 remediation Slice D, Task 3 (fix round): a member's role is an
 * *attribute* (who they are), not a *status* (a state that changes, or that
 * has a health/urgency reading) — DESIGN.md's dot+label rule is written for
 * status. A colored dot in front of "Proprietar"/"Administrator"/"Utilizator"
 * would imply a state distinction (ok/warn/bad) that doesn't exist here, so
 * this deliberately does NOT become a `StatusLabel`. It also used to be a
 * `Pill` (a wash-filled chip, out of place in a data row regardless), and
 * that chip spent `accent`/teal on "Proprietar" — DESIGN.md reserves teal
 * for primary actions, active navigation, selection, and live-processing
 * states, not a role marker. Plain text, same weight as the row's other
 * columns.
 */
function RoleLabel({ role }: { role: TeamMember['role'] }) {
  // `role` is server-supplied raw text typed as the known union at the API
  // boundary, but an unexpected value would otherwise throw indexing
  // ROLE_LABEL here — fall back to the raw text so the row still renders.
  return <span className="text-ink-2">{ROLE_LABEL[role] ?? role}</span>;
}

const inviteSchema = z.object({
  email: z.string().trim().min(1, 'Introduceți adresa de email').email('Adresă de email invalidă'),
});
type InviteFields = z.infer<typeof inviteSchema>;

const DUPLICATE_INVITE_MESSAGE = 'Există deja un membru sau o invitație pentru acest email.';

/** Surfaces the server's 409 (duplicate member/pending invitation, see
 *  `POST /echipa/invitations`) as the fixed RO message; any other error
 *  falls back to its own text. */
function messageFor(err: unknown): string {
  if (err instanceof ApiClientError) {
    return err.status === 409 ? DUPLICATE_INVITE_MESSAGE : err.message;
  }
  return 'A apărut o eroare neașteptată.';
}

/** For row/section action failures (remove member, toggle permission, cancel
 *  invitation, resend invitation) — no 409 special-case like `messageFor`'s
 *  invite form, so any `ApiClientError` shows its own message (the server's
 *  `{ error }` contract is always a clean RO string per CLAUDE.md); anything
 *  else (network failure, etc.) falls back to the fixed per-action text. */
function actionErrorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiClientError ? err.message : fallback;
}

function RowSkeleton({ cols }: { cols: number }) {
  return (
    <tr className="border-t border-rule-soft">
      <td className="px-5 py-3 sm:px-6" colSpan={cols}>
        <div className="h-4 w-full animate-pulse rounded-md bg-wash" aria-hidden />
      </td>
    </tr>
  );
}

/** Inline (not modal) invite form — Direction B, matching `AddEntryForm` in
 *  `NomenclatorPage.tsx`. Role is fixed „Utilizator" (the server always
 *  creates `member`-role invitations) so it's shown, not editable. */
function InviteForm() {
  const inviteMutation = useInvite();
  const [formError, setFormError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ email: string; link: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<InviteFields>({ resolver: zodResolver(inviteSchema), defaultValues: { email: '' } });

  async function onSubmit(values: InviteFields) {
    setFormError(null);
    try {
      const res = await inviteMutation.mutateAsync(values.email);
      reset();
      setCreated({ email: res.email, link: res.link });
      setCopied(false);
    } catch (err) {
      setCreated(null);
      setFormError(messageFor(err));
    }
  }

  async function copyLink() {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.link);
      setCopied(true);
    } catch {
      // Clipboard API unavailable/denied — the link stays selectable in the input below.
    }
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      className="flex flex-wrap items-end gap-3"
    >
      <div className="flex flex-col gap-1">
        <Label htmlFor="invite-email">Email</Label>
        <Input
          id="invite-email"
          type="email"
          autoComplete="off"
          className="h-8 w-64"
          {...register('email')}
          {...fieldAria(!!errors.email, 'invite-email-error')}
        />
        {errors.email && <p id="invite-email-error" className="text-[12.5px] text-bad">{errors.email.message}</p>}
      </div>
      <div className="flex flex-col gap-1">
        <Label className="text-[12.5px] text-ink-3">Rol</Label>
        <div className="flex h-8 items-center text-[13.5px] text-ink-3">Utilizator</div>
      </div>
      <Button type="submit" size="sm" disabled={isSubmitting}>
        {!isSubmitting && <UserPlus />}
        {isSubmitting ? 'Se trimite…' : 'Invită'}
      </Button>
      {formError && <p className="w-full text-[12.5px] text-bad">{formError}</p>}
      {created && (
        <div className="mt-1 flex w-full flex-wrap items-center gap-2 rounded-md border border-rule-soft bg-wash p-2 text-[12.5px]">
          <span className="text-ink-2">
            Invitație creată pentru <strong className="text-ink">{created.email}</strong>. Link:
          </span>
          <Input
            readOnly
            value={created.link}
            onFocus={(e) => e.currentTarget.select()}
            aria-label="Link invitație"
            className="h-7 min-w-[220px] flex-1 font-mono text-[12.5px]"
          />
          <button
            type="button"
            onClick={() => void copyLink()}
            title={copied ? 'Copiat!' : 'Copiază linkul'}
            aria-label={copied ? 'Link copiat' : 'Copiază linkul'}
            className={`flex size-8 shrink-0 items-center justify-center rounded transition-colors ${
              copied ? 'text-ok' : 'text-ink-3 hover:text-teal'
            }`}
          >
            {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          </button>
        </div>
      )}
    </form>
  );
}

function InvitationsSection() {
  const { data: invitations, isPending, isError } = useInvitations();
  const cancelMutation = useCancelInvitation();
  const resendMutation = useResendInvitation();
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);
  const [resentLinks, setResentLinks] = useState<Record<string, string>>({});

  const list = invitations ?? [];

  async function handleResend(inv: TeamInvitation) {
    try {
      const res = await resendMutation.mutateAsync(inv.id);
      setResentLinks((prev) => ({ ...prev, [inv.id]: res.link }));
    } catch {
      // Failure is tracked reactively via `resendMutation.isError` below
      // (rendered as an inline `text-bad` row) — the same
      // isError+variables-match pattern `NomenclatorPage.tsx` uses for its
      // `deleteFailed` row. The „Retrimite" link simply stays available to
      // try again.
    }
  }

  return (
    <div className="mt-6">
      <Section title="Invitații">
        <InviteForm />

        {isError && <p className="mt-4 text-[13.5px] text-bad">Nu s-au putut încărca invitațiile.</p>}

        {!isError && isPending && (
          <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[560px] text-[13.5px]">
            <tbody>
              <RowSkeleton cols={4} />
            </tbody>
          </table>
          </div>
        )}

        {!isError && !isPending && list.length === 0 && (
          <p className="mt-4 text-[13.5px] text-ink-3">Nicio invitație în așteptare.</p>
        )}

        {!isError && !isPending && list.length > 0 && (
          <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[560px] text-[13.5px]">
            <thead>
              <tr className="border-b border-rule text-left text-[11.5px] font-bold uppercase tracking-[0.05em] text-ink-3">
                <th className="px-5 py-2.5 sm:px-6">Email</th>
                <th className="px-4 py-2.5">Expiră</th>
                <th className="px-4 py-2.5 pr-5 text-right sm:pr-6">Acțiuni</th>
              </tr>
            </thead>
            <tbody>
              {list.map((inv) => {
                const isConfirmingCancel = confirmCancelId === inv.id;
                const cancelPending = cancelMutation.isPending && cancelMutation.variables === inv.id;
                const resendPending = resendMutation.isPending && resendMutation.variables === inv.id;
                const cancelFailed = cancelMutation.isError && cancelMutation.variables === inv.id;
                const resendFailed = resendMutation.isError && resendMutation.variables === inv.id;
                const resentLink = resentLinks[inv.id];

                return (
                  <Fragment key={inv.id}>
                    <tr className="border-t border-rule-soft align-top hover:bg-wash">
                      <td className="px-5 py-2.5 text-ink sm:px-6">{inv.email}</td>
                      <td className="px-4 py-2.5 tabular-nums text-ink-2">{formatDateRo(new Date(inv.expiresAt))}</td>
                      <td className="px-4 py-2.5 pr-5 sm:pr-6">
                        <div className="flex items-center justify-end gap-3">
                          {isConfirmingCancel ? (
                            <span className="inline-flex items-center gap-2">
                              <span className="text-ink-3">Sigur?</span>
                              <button
                                type="button"
                                onClick={() => {
                                  cancelMutation.mutate(inv.id);
                                  setConfirmCancelId(null);
                                }}
                                disabled={cancelPending}
                                className="text-[13.5px] font-medium text-bad hover:underline disabled:opacity-50"
                              >
                                Da, anulează
                              </button>
                              <button
                                type="button"
                                onClick={() => setConfirmCancelId(null)}
                                className="text-[13.5px] font-medium text-ink-3 hover:text-ink-2"
                              >
                                Nu
                              </button>
                            </span>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => void handleResend(inv)}
                                disabled={resendPending}
                                className="text-[13.5px] font-medium text-ink-2 hover:text-ink disabled:opacity-50"
                              >
                                {resendPending ? 'Se retrimite…' : 'Retrimite'}
                              </button>
                              <button
                                type="button"
                                onClick={() => setConfirmCancelId(inv.id)}
                                className="text-[13.5px] font-medium text-bad hover:underline"
                              >
                                Anulează
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {resentLink && (
                      <tr>
                        <td colSpan={3} className="px-5 pb-2.5 pt-0 sm:px-6">
                          <div className="flex flex-wrap items-center gap-2 rounded-md border border-rule-soft bg-wash p-2 text-[12.5px]">
                            <span className="text-ink-2">Link retrimis:</span>
                            <Input
                              readOnly
                              value={resentLink}
                              onFocus={(e) => e.currentTarget.select()}
                              aria-label={`Link retrimis pentru ${inv.email}`}
                              className="h-7 min-w-[220px] flex-1 font-mono text-[12.5px]"
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                    {!isConfirmingCancel && cancelFailed && (
                      <tr>
                        <td colSpan={3} className="px-5 pb-2.5 pt-0 text-[12.5px] text-bad sm:px-6">
                          {actionErrorMessage(cancelMutation.error, 'Anularea invitației a eșuat.')}
                        </td>
                      </tr>
                    )}
                    {!isConfirmingCancel && resendFailed && (
                      <tr>
                        <td colSpan={3} className="px-5 pb-2.5 pt-0 text-[12.5px] text-bad sm:px-6">
                          {actionErrorMessage(resendMutation.error, 'Retrimiterea a eșuat.')}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </Section>
    </div>
  );
}

export function EchipaPage() {
  const me = useMe();
  const isAdmin = me.data?.orgRole === 'owner' || me.data?.orgRole === 'admin';

  const { data: members, isPending, isError } = useMembers();
  const removeMutation = useRemoveMember();
  const permissionMutation = useSetMemberPermission();
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  const list = members ?? [];
  const ownerCount = list.filter((m) => m.role === 'owner').length;
  const cols = isAdmin ? 4 : 3;

  function togglePermission(member: TeamMember) {
    const current = member.permissions ?? [];
    const has = current.includes(NOMENCLATOR_EDIT);
    const next = has ? current.filter((p) => p !== NOMENCLATOR_EDIT) : [...current, NOMENCLATOR_EDIT];
    permissionMutation.mutate({ memberId: member.memberId, permissions: next });
  }

  return (
    <>
      <DocTop />
      <div className="mt-8">
        <div>
          <h1 className="text-[32px] font-[650] text-ink">Echipă</h1>
          <p className="mt-1 text-[13.5px] text-ink-3">
            {isPending ? 'Se încarcă…' : `${formatNumberRo(list.length)} membri`}
          </p>
        </div>
        <div className="mt-1 border-b-2 border-ink pb-3" />

        <div className="mt-6">
          <Section title="Membri">
            {isError && <p className="text-[13.5px] text-bad">Nu s-a putut încărca echipa.</p>}

            {!isError && isPending && (
              <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-[13.5px]">
                <tbody>
                  <RowSkeleton cols={cols} />
                  <RowSkeleton cols={cols} />
                  <RowSkeleton cols={cols} />
                </tbody>
              </table>
              </div>
            )}

            {!isError && !isPending && list.length === 0 && (
              <p className="text-[13.5px] text-ink-3">Niciun membru găsit.</p>
            )}

            {!isError && !isPending && list.length > 0 && (
              <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-[13.5px]">
                <thead>
                  <tr className="border-b border-rule text-left text-[11.5px] font-bold uppercase tracking-[0.05em] text-ink-3">
                    <th className="px-5 py-2.5 sm:px-6">Nume</th>
                    <th className="px-4 py-2.5">Email</th>
                    <th className="px-4 py-2.5">Rol</th>
                    {isAdmin && <th className="px-4 py-2.5 pr-5 text-right sm:pr-6">Acțiuni</th>}
                  </tr>
                </thead>
                <tbody>
                  {list.map((member) => {
                    const isSelf = member.userId === me.data?.userId;
                    const isLastOwner = member.role === 'owner' && ownerCount <= 1;
                    const canRemove = isAdmin && !isSelf && !isLastOwner;
                    const isConfirmingRemove = confirmRemoveId === member.memberId;
                    const removePending = removeMutation.isPending && removeMutation.variables === member.memberId;
                    const permissionPending =
                      permissionMutation.isPending && permissionMutation.variables?.memberId === member.memberId;
                    const removeFailed = removeMutation.isError && removeMutation.variables === member.memberId;
                    const permissionFailed =
                      permissionMutation.isError && permissionMutation.variables?.memberId === member.memberId;
                    // Owner/admin rows implicitly have every permission (the server's
                    // `hasPermission` = isOrgAdminRole OR flag) — they never reach the
                    // checkbox branch below, so this only matters for `member` rows.
                    // `permissions` is nullable (see `TeamMember`'s doc comment).
                    const hasEditPermission = (member.permissions ?? []).includes(NOMENCLATOR_EDIT);

                    return (
                      <Fragment key={member.memberId}>
                        <tr className="border-t border-rule-soft align-top hover:bg-wash">
                          <td className="px-5 py-2.5 text-ink sm:px-6">{member.name}</td>
                          <td className="px-4 py-2.5 text-ink-2">{member.email}</td>
                          <td className="px-4 py-2.5">
                            <RoleLabel role={member.role} />
                          </td>
                          {isAdmin && (
                            <td className="px-4 py-2.5 pr-5 sm:pr-6">
                              <div className="flex items-center justify-end gap-4">
                                {member.role === 'member' ? (
                                  <label className="inline-flex items-center gap-1.5 text-[12.5px] text-ink-2">
                                    <input
                                      type="checkbox"
                                      checked={hasEditPermission}
                                      onChange={() => togglePermission(member)}
                                      disabled={permissionPending}
                                      aria-label={`Editare nomenclator pentru ${member.name}`}
                                      className="size-4 accent-teal"
                                    />
                                    Editare nomenclator
                                  </label>
                                ) : (
                                  <span className="text-[12.5px] text-ink-3">inclus în rol</span>
                                )}

                                {canRemove ? (
                                  isConfirmingRemove ? (
                                    <span className="inline-flex items-center gap-2">
                                      <span className="text-ink-3">Sigur?</span>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          removeMutation.mutate(member.memberId);
                                          setConfirmRemoveId(null);
                                        }}
                                        disabled={removePending}
                                        className="text-[13.5px] font-medium text-bad hover:underline disabled:opacity-50"
                                      >
                                        Da, elimină
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setConfirmRemoveId(null)}
                                        className="text-[13.5px] font-medium text-ink-3 hover:text-ink-2"
                                      >
                                        Nu
                                      </button>
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => setConfirmRemoveId(member.memberId)}
                                      className="text-[13.5px] font-medium text-bad hover:underline"
                                    >
                                      Elimină
                                    </button>
                                  )
                                ) : (
                                  <span className="w-[76px]" aria-hidden />
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                        {isAdmin && !isConfirmingRemove && removeFailed && (
                          <tr>
                            <td colSpan={cols} className="px-5 pb-2.5 pt-0 text-[12.5px] text-bad sm:px-6">
                              {actionErrorMessage(removeMutation.error, 'Eliminarea a eșuat.')}
                            </td>
                          </tr>
                        )}
                        {isAdmin && permissionFailed && (
                          <tr>
                            <td colSpan={cols} className="px-5 pb-2.5 pt-0 text-[12.5px] text-bad sm:px-6">
                              {actionErrorMessage(permissionMutation.error, 'Actualizarea permisiunii a eșuat.')}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </Section>
        </div>

        {isAdmin && <InvitationsSection />}
      </div>
    </>
  );
}
